/**
 * Protected Route Component
 * Uses local authentication store.
 * Includes route-level RBAC: the route's required READ permission is checked
 * against the user's resolved permission keys (state.auth.permissions), the same
 * flat array the per-button usePermissions hooks read.
 */

import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchCurrentUser, fetchPermissions } from '../../features/auth/authSlice';
import { getJobForRoute } from '../../config/route-permissions';
import { JOB_NAMES } from '../../constants/jobNames';
import { permissionKeyFor } from '../../constants/permissions';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

// route-permissions stores normalized job strings (e.g. 'aimodelmaster'); bridge
// them back to the canonical JOB_NAMES value ('AI_MODEL_MASTER') that
// JOB_PERMISSIONS is keyed by, so a route resolves to its READ permission key.
const normalizeJob = (s: string): string => s.toLowerCase().replace(/[-_\s]/g, '');
const NORMALIZED_TO_JOB: Record<string, string> = Object.values(JOB_NAMES).reduce(
  (acc, value) => {
    acc[normalizeJob(value)] = value;
    return acc;
  },
  {} as Record<string, string>
);

/**
 * The READ permission key required to view a path, or null when the route is
 * public/unmapped or the job grants no READ (fail-open — never block an
 * unmapped route).
 */
const requiredReadPermission = (pathname: string): string | null => {
  const jobNorm = getJobForRoute(pathname); // '' = public, null = unmapped
  if (!jobNorm) return null;
  const jobName = NORMALIZED_TO_JOB[jobNorm];
  return jobName ? permissionKeyFor(jobName, 'read') : null;
};

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { isAuthenticated, user, loading, token, permissionsLoaded, permissions } = useAppSelector(
    (state) => state.auth
  );

  // Fetch user data if authenticated but user data is missing
  useEffect(() => {
    if (isAuthenticated && !user && token) {
      dispatch(fetchCurrentUser());
    }
  }, [isAuthenticated, user, token, dispatch]);

  // Bootstrap resolved permissions on load / refresh (Redux is in-memory, so a
  // page reload starts empty even though the token persists in localStorage).
  useEffect(() => {
    if (isAuthenticated && token && !permissionsLoaded) {
      dispatch(fetchPermissions());
    }
  }, [isAuthenticated, token, permissionsLoaded, dispatch]);

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Show loading spinner while fetching user data
  if (isAuthenticated && !user && loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-canvas dark:bg-gray-900">
        <ProgressSpinner style={{ width: '50px', height: '50px' }} />
      </div>
    );
  }

  // Route-level RBAC. Only enforced for routes mapped to a known READ permission;
  // unmapped/public routes always pass (fail-open).
  const requiredPermission = requiredReadPermission(location.pathname);
  if (requiredPermission) {
    // Wait for the first permission fetch so we never redirect on empty/stale data.
    if (!permissionsLoaded) {
      return (
        <div className="flex justify-center items-center min-h-screen bg-canvas dark:bg-gray-900">
          <ProgressSpinner style={{ width: '50px', height: '50px' }} />
        </div>
      );
    }
    if (!permissions.includes(requiredPermission)) {
      // Missing the READ permission for this screen → send to the dashboard
      // (a public route, so no redirect loop).
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
